import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import PublicContentPageShell from '@components/layout/PublicContentPageShell';
import DashboardSubscriptionPanel from '@components/subscription/DashboardSubscriptionPanel';
import { brandColors } from '@config/brandTokens';
import { useAuthStore } from '@store/authStore';
import {
  DEFAULT_SUBSCRIPTION_OFFERS,
  fetchSubscriptionOffers,
  formatUsdYearly,
  getEffectiveYearlyPrice,
  isPurchasableOffer,
  purchasePathForOffer,
  type SubscriptionOffer,
} from '@/content/subscriptionPricing';
import {
  earlyBirdUsuallyLine,
  inAppDashboardPath,
  inAppPlansPath,
  preferredPaymentGateway,
  purchaseGatewayPath,
} from '@/content/subscriptionCatalog';
import { writeSubscriptionCheckoutDraft } from '@/utils/subscriptionCheckout';
import type { UserSubscription } from '@types/index';

const SubscriptionPurchasePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuthStore();
  const [offers, setOffers] = useState<SubscriptionOffer[]>(DEFAULT_SUBSCRIPTION_OFFERS);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<UserSubscription | null>(
    user?.subscription?.status === 'active' ? user.subscription : null,
  );

  const planFromQuery = String(searchParams.get('plan') || '').trim();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const data = await fetchSubscriptionOffers();
      if (!cancelled) {
        setOffers(data);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const selected = useMemo(() => {
    return (
      offers.find((o) => o.id === planFromQuery) ||
      offers.find((o) => o.id === user?.subscription?.suggestedOfferId) ||
      offers.find((o) => o.id === 'players_parents') ||
      offers[0]
    );
  }, [offers, planFromQuery, user?.subscription?.suggestedOfferId]);

  const price = selected ? getEffectiveYearlyPrice(selected) : null;

  const handlePurchase = async () => {
    if (!selected) return;
    if (!user) {
      navigate(`/login?returnUrl=${encodeURIComponent(`/subscriptions/purchase?plan=${selected.id}`)}`);
      return;
    }
    if (!isPurchasableOffer(selected)) {
      navigate(purchasePathForOffer(selected));
      return;
    }
    if (price?.payableUsd == null) {
      setError('Choose a yearly plan to continue.');
      return;
    }
    writeSubscriptionCheckoutDraft({
      offerId: selected.id,
      amount: price.payableUsd,
      currency: selected.currency || 'USD',
      planLabel: selected.id === 'schools' ? 'Schools/Colleges' : selected.name,
      listUsd: price.listUsd,
      discountPercent: price.discountPercent,
      earlyBirdActive: price.earlyBirdActive,
      provider: preferredPaymentGateway(selected.currency),
    });
    navigate(
      purchaseGatewayPath({
        role: user.role,
        amount: price.payableUsd,
        plan: selected.id === 'schools' ? 'Schools/Colleges' : selected.name,
        returnUrl: inAppDashboardPath(user.role),
        currency: selected.currency,
      }),
    );
  };

  if (user) {
    return (
      <PublicContentPageShell
        title="Purchase options"
        subtitle="Your yearly options are based on this account — not the public marketing list."
        maxWidth="md"
      >
        <DashboardSubscriptionPanel />
        <Button
          component={RouterLink}
          to={inAppPlansPath(user.role)}
          sx={{ mt: 2, textTransform: 'none', fontWeight: 700, color: brandColors.navy }}
        >
          Back to dashboard plans
        </Button>
      </PublicContentPageShell>
    );
  }

  return (
    <PublicContentPageShell
      title="Purchase options"
      subtitle="Choose a yearly EncycGames plan. Player / Parent has a listed price. Academies, Schools/Colleges, and Corporate are quoted after a short sales conversation."
      maxWidth="md"
    >
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2.5 }}>
            {offers.map((offer) => {
              const active = offer.id === selected?.id;
              return (
                <Chip
                  key={offer.id}
                  label={offer.id === 'schools' ? 'Schools/Colleges' : offer.name}
                  onClick={() => navigate(`/subscriptions/purchase?plan=${offer.id}`, { replace: true })}
                  sx={{
                    fontWeight: 800,
                    bgcolor: active ? brandColors.navy : brandColors.ice,
                    color: active ? brandColors.white : brandColors.navy,
                    '&:hover': { bgcolor: active ? '#0a0f28' : '#e2e8f0' },
                  }}
                />
              );
            })}
          </Stack>

          {selected && (
            <Box
              sx={{
                p: { xs: 2.5, md: 3 },
                borderRadius: 3,
                bgcolor: brandColors.white,
                border: `1px solid ${selected.highlight ? brandColors.navy : '#e2e8f0'}`,
              }}
            >
              <Typography
                sx={{
                  fontFamily: '"Sora", Arial, sans-serif',
                  fontWeight: 800,
                  fontSize: '1.35rem',
                  color: brandColors.navy,
                }}
              >
                {selected.id === 'schools' ? 'Schools/Colleges' : selected.name}
              </Typography>
              <Typography sx={{ color: brandColors.slate, mt: 0.35, mb: 1.5 }}>
                {selected.audience}
              </Typography>

              {isPurchasableOffer(selected) && price?.payableUsd != null ? (
                <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 1.5 }}>
                  <Typography
                    sx={{
                      fontFamily: '"Sora", Arial, sans-serif',
                      fontWeight: 900,
                      fontSize: '2.2rem',
                      color: brandColors.navy,
                      lineHeight: 1,
                    }}
                  >
                    {formatUsdYearly(price.payableUsd)}
                  </Typography>
                  <Typography sx={{ color: brandColors.slate, fontWeight: 600 }}>/ year</Typography>
                </Stack>
              ) : (
                <Typography
                  sx={{
                    fontFamily: '"Sora", Arial, sans-serif',
                    fontWeight: 900,
                    fontSize: '1.8rem',
                    color: brandColors.navy,
                    mb: 1.5,
                  }}
                >
                  Custom quote
                </Typography>
              )}

              {price?.earlyBirdActive && price.listUsd != null && (
                <Typography
                  variant="body2"
                  sx={{ color: brandColors.slate, fontSize: '0.85rem', fontWeight: 600, mb: 1.5 }}
                >
                  {earlyBirdUsuallyLine(price.discountPercent, formatUsdYearly(price.listUsd))}
                </Typography>
              )}

              <Stack spacing={0.85} sx={{ mb: 2.5 }}>
                {selected.features.slice(0, 5).map((feature) => (
                  <Stack key={feature} direction="row" spacing={1} alignItems="flex-start">
                    <CheckIcon sx={{ fontSize: 18, color: brandColors.blue, mt: '2px' }} />
                    <Typography sx={{ fontSize: '0.92rem', color: '#1e293b' }}>{feature}</Typography>
                  </Stack>
                ))}
              </Stack>

              {result?.status === 'active' && selected.id === result.offerId && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Yearly plan is active
                  {result.expiresAt
                    ? ` until ${new Date(result.expiresAt).toLocaleDateString()}`
                    : ''}
                  . Manage cards from Billing & invoices.
                </Alert>
              )}

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Alert severity="info" sx={{ mb: 2 }}>
                Sign in to complete a purchase. After sign-in, plans live on your dashboard — not this public list.
              </Alert>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                {isPurchasableOffer(selected) ? (
                  <Button
                    variant="contained"
                    disabled={buying || result?.status === 'active'}
                    onClick={handlePurchase}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 800,
                      bgcolor: brandColors.orange,
                      '&:hover': { bgcolor: '#f07a3d' },
                    }}
                  >
                    {buying ? 'Confirming…' : 'Sign in to purchase'}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={() => navigate(purchasePathForOffer(selected))}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 800,
                      bgcolor: brandColors.navy,
                      '&:hover': { bgcolor: '#0a0f28' },
                    }}
                  >
                    Contact sales
                  </Button>
                )}
                <Button
                  component={RouterLink}
                  to="/subscriptions"
                  sx={{ textTransform: 'none', fontWeight: 700, color: brandColors.navy }}
                >
                  Back to all plans
                </Button>
              </Stack>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, lineHeight: 1.5 }}>
                {selected.termsNote || '*Terms & conditions apply.'} Cards are managed from Billing & invoices
                through Stripe or Razorpay.
              </Typography>
            </Box>
          )}
        </>
      )}
    </PublicContentPageShell>
  );
};

export default SubscriptionPurchasePage;
